import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { DailyRosterEntry, NotifiedInAdvance } from '../api/hooks.js';
import {
  usePutAttendance,
  usePatchNotified,
  useEntities,
  useDecisionTypes,
} from '../api/hooks.js';
import { hhmmFromMinutes, isPendingRow, avatarColor, decisionPill, attendanceWriteError } from '../lib/saisie.js';
import { toast } from '../store/toast.js';
import { useAutosaveFlush } from '../lib/unsaved.js';
import { PunchTime } from './PunchTime.js';

// Saisie quotidienne row table (redesign). Columns: Employé · Pointage
// · Heures · Décision · Prévenu? · Note. The Décision column is a
// read-only display (proto): a neutral « — » for a plain-present day,
// or a tone-coloured pill (+ author-initials chip for human decisions).
// All overriding goes through the guided arbitrage — every row clicks
// into it. The Prévenu? picker and the Note stay inline-editable.

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

interface Props {
  date: string;
  rows: DailyRosterEntry[];
  /** Click a pending row → launch arbitrage at that employee. */
  onLaunch?: (employeeId: string) => void;
  /** The day is signed off (or under review by someone else): the server
   * refuses every write, so the inline fields stop pretending otherwise. */
  readOnly?: boolean;
  /** Why, in one line — shown as the title of the disabled fields. */
  readOnlyHint?: string;
}

export function DailyEntryTable({ date, rows, onLaunch, readOnly = false, readOnlyHint }: Props) {
  const { data: entities } = useEntities();
  const { data: decisionTypes } = useDecisionTypes();
  const colorByEntity = new Map((entities ?? []).map((e) => [e.id, e.color]));
  const customLabel = (code: string) => decisionTypes?.find((d) => d.code === code)?.label;

  if (rows.length === 0) {
    return <p className="page-placeholder">Aucun salarié actif pour ce filtre.</p>;
  }

  const pending = rows.filter(isPendingRow);
  const done = rows.filter((r) => !isPendingRow(r));
  const renderRow = (r: DailyRosterEntry) => (
    <EntryRow
      key={r.employee.id}
      date={date}
      row={r}
      color={colorByEntity.get(r.employee.entityId) ?? 'var(--shf)'}
      customLabel={customLabel}
      onLaunch={onLaunch}
      readOnly={readOnly}
      readOnlyHint={readOnlyHint}
    />
  );
  return (
    <div className="card" style={{ padding: 0 }} data-testid="entry-table">
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: '22%' }}>Employé</th>
            <th style={{ width: '14%' }}>Pointage</th>
            <th className="num" style={{ width: '12%' }}>
              Heures
            </th>
            <th style={{ width: '17%' }}>
              Décision{' '}
              <span style={{ textTransform: 'none', letterSpacing: 0, color: 'var(--ink-4)', fontWeight: 500, fontSize: 10 }}>
                (Lamis / Bouchra)
              </span>
            </th>
            <th style={{ width: '17%' }}>Prévenu&nbsp;?</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {pending.length > 0 && (
            <tr className="sq-group-head">
              <td colSpan={6}>
                <span className="sq-group-tag todo">À arbitrer · {pending.length}</span>
              </td>
            </tr>
          )}
          {pending.map(renderRow)}
          {done.length > 0 && (
            <tr className="sq-group-head">
              <td colSpan={6}>
                <span className="sq-group-tag done">Traité · {done.length}</span>
              </td>
            </tr>
          )}
          {done.map(renderRow)}
        </tbody>
      </table>
    </div>
  );
}

function EntryRow({
  date,
  row,
  color,
  customLabel,
  onLaunch,
  readOnly,
  readOnlyHint,
}: {
  date: string;
  row: DailyRosterEntry;
  color: string;
  customLabel: (code: string) => string | undefined;
  onLaunch?: (employeeId: string) => void;
  readOnly?: boolean;
  readOnlyHint?: string;
}) {
  const put = usePutAttendance();
  const notified = usePatchNotified();
  const rec = row.record;

  const [note, setNote] = useState(rec?.note ?? '');
  useEffect(() => {
    setNote(rec?.note ?? '');
  }, [rec?.note]);

  const noteDebounced = useDebouncedCallback((value: string) => {
    put.mutate(
      { date, employeeId: row.employee.id, update: { note: value || null } },
      { onError: (err) => toast.danger(attendanceWriteError(err, "Échec de l'enregistrement de la note.")) },
    );
  }, 500);

  // Materialise a record id for a row that has none (true absence)
  // so the workflow PATCH has something to target.
  async function ensureRecordId(): Promise<string | null> {
    if (rec?.id) return rec.id;
    try {
      const created = await put.mutateAsync({ date, employeeId: row.employee.id, update: {} });
      return created.id;
    } catch {
      toast.danger("Impossible de créer la ligne de pointage.");
      return null;
    }
  }

  const onPickNotified = async (value: string) => {
    const id = await ensureRecordId();
    if (!id) return;
    notified.mutate(
      { recordId: id, date, notifiedInAdvance: (value || null) as NotifiedInAdvance | null },
      { onError: (err) => toast.danger(attendanceWriteError(err, 'Échec de la réponse « prévenu ? ».')) },
    );
  };

  const onNotifiedDetail = useDebouncedCallback((value: string) => {
    if (!rec?.id) return;
    notified.mutate(
      {
        recordId: rec.id,
        date,
        notifiedInAdvance: rec.notifiedInAdvance,
        notifiedDetail: value || null,
      },
      { onError: (err) => toast.danger(attendanceWriteError(err, 'Échec du détail.')) },
    );
  }, 500);

  // Une note ou un détail encore dans son délai de 500 ms part tout de
  // suite quand on quitte (société, page, onglet) ou au démontage.
  useAutosaveFlush(`entry-row-${row.employee.id}`, () => {
    noteDebounced.flush();
    onNotifiedDetail.flush();
  });

  const hasLateTag = row.tags.some((t) => t.type === 'late');
  // A row needs attention when a Décision or a Prévenu? answer is still
  // missing — or it is a half-clocked, undecided day. Single source of
  // truth: isPendingRow.
  const needsAttention = isPendingRow(row);
  // Every row clicks into arbitrage (override path); pending+undecided
  // shows the « + Définir » affordance instead of a read pill.
  const launch = onLaunch && !readOnly ? () => onLaunch(row.employee.id) : undefined;
  const undecided = !row.record?.decisionType && !row.autoDecision;

  return (
    <tr
      data-testid={`entry-row-${row.employee.id}`}
      className={needsAttention ? 'row-need sq-row-pending' : undefined}
    >
      {/* Employé (state rail = entity colour; avatar = per-person colour) */}
      <td className="sq-rail" style={{ '--rail': color } as CSSProperties} onClick={launch}>
        <div className="empcell">
          <div className="empcell-avatar" style={{ background: avatarColor(row.employee.id) }} aria-hidden="true">
            {initials(row.employee.name)}
          </div>
          <div>
            <div className="empcell-name">{row.employee.name}</div>
            <div className="empcell-meta">{row.employee.code}</div>
          </div>
        </div>
      </td>

      {/* Pointage */}
      <td onClick={row.recordStatus.isIncomplete ? undefined : launch}>
        <PointageCell date={date} row={row} readOnly={readOnly} />
      </td>

      {/* Heures + tags */}
      <td className="num" onClick={launch}>
        <HeuresCell row={row} />
      </td>

      {/* Décision — read-only: « + Définir » launcher for a pending,
          undecided exception; otherwise a neutral dash (plain present)
          or a tone pill + author chip. The whole cell clicks into
          arbitrage so the override path survives. */}
      <td onClick={needsAttention && undecided ? undefined : launch}>
        {needsAttention && undecided && !readOnly ? (
          <button
            type="button"
            className="sq-define-btn"
            onClick={launch}
            data-testid={`decision-define-${row.employee.id}`}
          >
            + Définir
          </button>
        ) : (
          <DecisionDisplay row={row} customLabel={customLabel} />
        )}
      </td>

      {/* Prévenu ? */}
      <td>
        {row.autoDecision && !row.record?.decisionType ? (
          <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>—</span>
        ) : (
          <NotifiedCell
            row={row}
            required={row.needsNotified || row.notifiedRequired}
            hasLateTag={hasLateTag}
            onPick={onPickNotified}
            onDetail={onNotifiedDetail}
            readOnly={readOnly}
            readOnlyHint={readOnlyHint}
            testid={`notified-select-${row.employee.id}`}
          />
        )}
      </td>

      {/* Note */}
      <td>
        <input
          type="text"
          className="entry-input note-input"
          value={note}
          placeholder={readOnly ? '—' : '+ note'}
          disabled={readOnly}
          title={readOnly ? readOnlyHint : undefined}
          onChange={(e) => {
            setNote(e.target.value);
            noteDebounced(e.target.value);
          }}
          data-testid={`entry-note-${row.employee.id}`}
        />
      </td>
    </tr>
  );
}

function PointageCell({ date, row, readOnly }: { date: string; row: DailyRosterEntry; readOnly?: boolean }) {
  const put = usePutAttendance();
  const rec = row.record;
  const submit = (field: 'checkIn' | 'checkOut', value: string) => {
    if (!value) return;
    put.mutate(
      { date, employeeId: row.employee.id, update: { [field]: value } },
      { onError: (err) => toast.danger(attendanceWriteError(err, "Échec de l'enregistrement du pointage.")) },
    );
  };

  // One punch, correctable in place (ticket #31: a wrong badge used to
  // be untouchable once the device had written it). A correction keeps
  // the device reading beside it; a signed-off day stays read-only.
  const punch = (field: 'checkIn' | 'checkOut') => (
    <PunchTime
      label={field === 'checkIn' ? 'entrée' : 'sortie'}
      value={rec?.[field] ?? null}
      device={(field === 'checkIn' ? rec?.checkInDevice : rec?.checkOutDevice) ?? null}
      manual={(field === 'checkIn' ? rec?.checkInManual : rec?.checkOutManual) ?? false}
      editable={!readOnly}
      pending={put.isPending}
      onSave={(v) => submit(field, v)}
      testid={`punch-${field === 'checkIn' ? 'in' : 'out'}-${row.employee.id}`}
    />
  );

  // Both punches present → both times, each correctable.
  if (rec?.checkIn && rec?.checkOut) {
    const isLate = row.tags.some((t) => t.type === 'late');
    return (
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 12,
          color: isLate ? 'var(--warn)' : 'var(--ink-2)',
          fontWeight: isLate ? 600 : undefined,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {punch('checkIn')} → {punch('checkOut')}
      </span>
    );
  }

  // "Pointage incomplet" — exactly one punch on a working day. Let the
  // editor fill the missing time inline; it stores as a manual punch
  // (source='manual', *Manual flag) and reclassifies the row on refetch.
  if (row.recordStatus.isIncomplete) {
    const missing: 'checkIn' | 'checkOut' = rec?.checkIn ? 'checkOut' : 'checkIn';
    return (
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`pointage-incomplete-${row.employee.id}`}
        style={{ display: 'grid', gap: 4 }}
      >
        <span className="tag less" style={{ width: 'fit-content' }}>pointage incomplet</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--mono)', fontSize: 12 }}>
          {missing === 'checkIn' ? (
            <MissingPunch
              label="entrée" editable={!readOnly} disabled={put.isPending}
              onSubmit={(v) => submit('checkIn', v)} testid={`punch-in-${row.employee.id}`}
            />
          ) : (
            punch('checkIn')
          )}
          <span style={{ color: 'var(--ink-4)' }}>→</span>
          {missing === 'checkOut' ? (
            <MissingPunch
              label="sortie" editable={!readOnly} disabled={put.isPending}
              onSubmit={(v) => submit('checkOut', v)} testid={`punch-out-${row.employee.id}`}
            />
          ) : (
            punch('checkOut')
          )}
        </div>
      </div>
    );
  }

  const decision = rec?.decisionType ?? null;
  if (decision === 'TT') return <Placeholder text="— · télétravail" />;
  if (decision === 'CONGE' || decision === 'RECUP') return <Placeholder text="— · congé" />;
  if (decision === 'MALADIE') return <Placeholder text="— · maladie" />;
  if (decision === 'RDV_PERSO') return <Placeholder text="— · RDV perso" />;
  if (decision === 'AUTRE') return <Placeholder text="— · justifié" />;
  // No pointage, no decision → true absence.
  return (
    <span style={{ color: 'var(--bad)', fontSize: 12, fontFamily: 'var(--mono)' }}>
      — · aucun pointage
    </span>
  );
}

// The missing side of a half-clocked day: an inline time input, always
// open. Enter/blur submits the entered time.
function MissingPunch({
  label, editable, disabled, onSubmit, testid,
}: {
  label: string;
  editable: boolean;
  disabled?: boolean;
  onSubmit: (value: string) => void;
  testid: string;
}) {
  if (!editable) return <span>—</span>;
  return (
    <input
      type="time"
      defaultValue=""
      className="entry-input"
      disabled={disabled}
      style={{ width: 92, fontFamily: 'var(--mono)', fontSize: 12 }}
      onBlur={(e) => onSubmit(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      data-testid={testid}
      aria-label={`Saisir ${label} (pointage manuel)`}
    />
  );
}

function Placeholder({ text }: { text: string }) {
  return <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>{text}</span>;
}

function HeuresCell({ row }: { row: DailyRosterEntry }) {
  const mins = row.recordStatus.durationMinutes;
  const isAbsent = row.tags.some((t) => t.type === 'abs');
  // H:MM, not decimal (proto §1) — hhmmFromMinutes(588) → "9:48".
  const hours = hhmmFromMinutes(mins);
  const valClass = isAbsent ? 'hcell-val bad' : mins == null ? 'hcell-val muted' : 'hcell-val';
  // The ±delta tags (↑ over / ↓ short) drop to a clean 2nd line; the
  // status tag (Retard / pile / Absent) stays inline with the value.
  const isDelta = (t: { type: string }) => t.type === 'more' || t.type === 'less';
  const statusTags = row.tags.filter((t) => !isDelta(t));
  const deltaTags = row.tags.filter(isDelta);
  const tagEl = (t: { type: string; label: string }, i: number) => (
    <span key={`${t.type}-${i}`} className={`tag ${t.type === 'justified' ? 'tt' : t.type}`}>
      {t.label}
    </span>
  );
  return (
    <div className="hcell">
      <div className="hcell-line">
        <span className={valClass} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {hours}
        </span>
        {statusTags.map(tagEl)}
      </div>
      {deltaTags.length > 0 && <div className="hcell-delta">{deltaTags.map(tagEl)}</div>}
    </div>
  );
}

// Read-only Décision display (proto): plain-present → neutral « — »;
// any other effective decision → tone pill, plus an author-initials chip
// for human decisions (server-resolved decisionSetByName). Editing goes
// through arbitrage (the cell click).
function DecisionDisplay({
  row,
  customLabel,
}: {
  row: DailyRosterEntry;
  customLabel: (code: string) => string | undefined;
}) {
  const rec = row.record;
  const eff = row.effectiveDecision;
  const customCode = rec?.decisionCustomCode ?? null;

  // Plain present (auto or human-justified) → neutral dash.
  if (!eff || (eff === 'PRESENT' && !customCode)) {
    return <span className="sq-dec-dash" data-testid={`decision-dash-${row.employee.id}`}>—</span>;
  }
  const pill = decisionPill(eff, customCode, rec?.status ?? null, customLabel);
  if (!pill) return <span className="sq-dec-dash">—</span>;

  // Author chip only for an explicit (human) decision.
  const author = rec?.decisionType ? rec.decisionSetByName : null;
  return (
    <span className="sq-dec" data-testid={`decision-pill-${row.employee.id}`}>
      <span className={`sq-pill tone-${pill.tone}`}>{pill.label}</span>
      {author && rec?.decisionSetBy && (
        <span
          className="sq-author-chip"
          style={{ background: avatarColor(rec.decisionSetBy) }}
          title={`Décidé par ${author}`}
          data-testid={`decision-author-${row.employee.id}`}
        >
          {initials(author)}
        </span>
      )}
    </span>
  );
}

function NotifiedCell({
  row,
  required,
  hasLateTag,
  onPick,
  onDetail,
  readOnly,
  readOnlyHint,
  testid,
}: {
  row: DailyRosterEntry;
  required: boolean;
  hasLateTag: boolean;
  onPick: (value: string) => void;
  onDetail: (value: string) => void;
  readOnly?: boolean;
  readOnlyHint?: string;
  testid: string;
}) {
  const val = row.record?.notifiedInAdvance ?? '';
  const detail = row.record?.notifiedDetail ?? '';
  const requiredUnanswered = required && val === '';

  // Not required + unanswered → muted dash (+ optional hint).
  if (!required && val === '') {
    return (
      <div>
        <select
          className="notif-pick na"
          value=""
          disabled={readOnly}
          title={readOnly ? readOnlyHint : undefined}
          onChange={(e) => onPick(e.target.value)}
          data-testid={testid}
          aria-label="Prévenu ?"
        >
          <option value="">—</option>
          <option value="YES">Oui</option>
          <option value="NO">Non</option>
        </select>
        {hasLateTag && (
          <span style={{ fontSize: 10.5, color: 'var(--ink-4)', display: 'block' }}>
            &lt; 2h, optionnel
          </span>
        )}
      </div>
    );
  }

  const cls = requiredUnanswered
    ? 'notif-pick empty'
    : val === 'YES'
      ? 'notif-pick yes'
      : val === 'NO'
        ? 'notif-pick no'
        : 'notif-pick';

  return (
    <div>
      <select
        className={cls}
        value={val}
        disabled={readOnly}
        title={readOnly ? readOnlyHint : undefined}
        onChange={(e) => onPick(e.target.value)}
        data-testid={testid}
        aria-label="Prévenu ?"
      >
        <option value="">{requiredUnanswered ? 'Réponse requise' : '—'}</option>
        <option value="YES">Oui</option>
        <option value="NO">Non</option>
      </select>
      {(val === 'YES' || val === 'NO') && (
        <input
          type="text"
          className="notif-detail-input"
          defaultValue={detail}
          placeholder="détail (ex. SMS 7h30)"
          disabled={readOnly}
          title={readOnly ? readOnlyHint : undefined}
          onChange={(e) => onDetail(e.target.value)}
          data-testid={`notified-detail-${row.employee.id}`}
        />
      )}
    </div>
  );
}

// Local debounce — kept minimal to avoid adding a lodash dep. `flush()`
// fires the pending call immediately (and is a no-op when nothing waits).
function useDebouncedCallback<T extends (...args: never[]) => void>(fn: T, delayMs: number): T & { flush: () => void } {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<(() => void) | null>(null);
  const debounced = ((...args: Parameters<T>) => {
    if (ref.current) clearTimeout(ref.current);
    pending.current = () => fn(...args);
    ref.current = setTimeout(() => {
      ref.current = null;
      pending.current = null;
      fn(...args);
    }, delayMs);
  }) as T & { flush: () => void };
  debounced.flush = () => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
    const run = pending.current;
    pending.current = null;
    run?.();
  };
  return debounced;
}
